import React from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { ArrowUpIcons } from '../../../../../../../icons/icons';
import Pagination from '../../../../../../../ui/components/Common/DataTable/Paginaion';

export interface ColumnDef<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
  disableSort?: boolean;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface GridViewProps<T> {
  rowHeaders: ColumnDef<T>[];
  data: T[];
  columns: ColumnDef<T>[];
  sortConfig: SortConfig[];
  onSortChange: (key: string, dir: 'asc' | 'desc') => void;
  onItemClick: (item: T) => void;
  onDragEnd?: (items: T[]) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export const ResultsGridView = <T,>({
  rowHeaders,
  data,
  columns,
  sortConfig,
  onSortChange,
  onItemClick,
  onDragEnd,
  totalRecords,
  totalPages,
  rowsPerPage,
  onPageChange,
  currentPage,
}: GridViewProps<T>) => {
  const [items, setItems] = React.useState<T[]>(data);

  React.useEffect(() => {
    setItems(data);
  }, [data]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const updated = Array.from(items);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    setItems(updated);
    onDragEnd?.(updated);
  };

  return (
    <div className='flex flex-col gap-3 py-1'>
      { items.length === 0 ? (
        <div className='flex min-h-[180px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center'>
          <div>
            <p className='text-sm font-semibold text-slate-800'>
              No interview results yet
            </p>
            <p className='mt-1 max-w-[280px] text-xs text-slate-500'>
              Completed smart interview results will appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-start gap-3 lg:flex-row'>
          <div className='flex w-full overflow-x-auto rounded-lg bg-primary-background p-1 lg:w-44 lg:flex-col lg:overflow-visible'>
            { rowHeaders.map((col) => {
              const entry = sortConfig.find((s) => s.key === col.key);
              const isAsc = entry?.direction === 'asc';
              const isDesc = entry?.direction === 'desc';
              const nextDir = isAsc ? 'desc' : 'asc';

              return (
                <div
                    key={col.key}
                    className='group relative flex h-10 min-w-[140px] cursor-pointer select-none items-center border-b border-gray-200 px-2 text-xs font-medium lg:min-w-0'
                    onClick={() =>
                      !col.disableSort && onSortChange(col.key, nextDir)
                    }
                >
                  <span className='flex-1 truncate'>{ col.label }</span>

                  { !col.disableSort && (
                    <div
                        className={`absolute right-2 flex flex-col items-center opacity-0 transition-opacity duration-150 group-hover:opacity-100 ${
                          entry ? 'opacity-100' : ''
                        }`}
                    >
                      <ArrowUpIcons
                          className={`h-3.5 w-3.5 rotate-90 transform ${
                            isAsc ? 'text-primary-dark' : 'text-gray-400'
                          }`}
                      />
                      <ArrowUpIcons
                          className={`h-3.5 w-3.5 -rotate-90 transform ${
                            isDesc ? 'text-primary-dark' : 'text-gray-400'
                          }`}
                      />
                    </div>
                  ) }
                </div>
              );
            }) }
          </div>

          <div className='w-full flex-1 overflow-x-auto p-1'>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId='results' direction='horizontal'>
                { (prov) => (
                  <div
                      className='flex gap-3 pb-3'
                      {...prov.droppableProps}
                      ref={prov.innerRef}
                  >
                    { items.map((item, idx) => (
                      <Draggable
                          key={idx}
                          draggableId={`result-${idx}`}
                          index={idx}
                      >
                        { (p) => (
                          <div
                              ref={p.innerRef}
                              {...p.draggableProps}
                              {...p.dragHandleProps}
                              className='min-w-[240px] flex-shrink-0 cursor-pointer rounded bg-white outline outline-1 outline-gray-200'
                              onClick={() => onItemClick(item)}
                          >
                            { columns.map((col) => {
                              const raw = (item as any)[col.key];
                              let num = NaN;
                              if (typeof raw === 'number') num = raw;
                              else if (raw?.score != null) num = raw.score;
                              let bg = '';
                              if (!isNaN(num)) {
                                if (num >= 85) bg = 'bg-green-50';
                                else if (num >= 70) bg = 'bg-blue-50';
                                else if (num >= 50) bg = 'bg-orange-50';
                                else bg = 'bg-red-50';
                              }
                              return (
                                <div
                                    key={col.key}
                                    className={`flex h-10 items-center border-b border-gray-200 px-3 text-xs ${bg}`}
                                >
                                  { col.render
                                    ? col.render(item)
                                    : !isNaN(num)
                                      ? num
                                      : String(raw || '-') }
                                </div>
                              );
                            }) }
                          </div>
                        ) }
                      </Draggable>
                    )) }
                    { prov.placeholder }
                  </div>
                ) }
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      ) }

      { items.length > 0 && (
        <div className='flex items-center justify-center gap-4'>
          <Pagination
              totalRecords={totalRecords}
              currentPage={currentPage}
              handlePageChange={onPageChange}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              hasHidePagination={false}
          />
        </div>
      ) }
    </div>
  );
};
